import { useQuery } from '@apollo/client';
import { Button, Dropdown, Menu, Spin, Typography } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import Tree from 'react-virtualized-tree';
import Renderers from 'react-virtualized-tree/es/renderers';

import { useClientsState } from '@marketreach/providers/ClientsProvider';
import { TaxonomyContext } from '@marketreach/providers/TaxonomyProvider';
import { ATTRIBUTES } from '@marketreach/services/apollo/attributes';
import {
  flattenNodes,
  normalizeEntity,
  treeData,
} from '@marketreach/utils/common';

import 'react-virtualized/styles.css';
import 'react-virtualized-tree/lib/main.css';
import 'material-icons/css/material-icons.css';

import './styles.scss';

const NodeNameRenderer = ({ node: { name }, children }) => (
  <>
    {name}
    {children}
  </>
);

const { Deletable, Expandable, Favorite } = Renderers;

const SidebarAttributesTree = (props) => {
  const { selected: client } = useClientsState();

  const { context, setContext } = useContext(TaxonomyContext);
  const { attributes } = context;

  const [state, setState] = useState({
    nodes: [],
    availableRenderers: [Expandable, Deletable, Favorite],
    selectedRenderers: [Expandable, NodeNameRenderer],
  });

  const {
    selectedKeys,
    expandedKeys,
    handleExpandedKeys,
    handleSelectedKeys,
    handleClickSubmenu,
  } = props;

  const {
    loading: loadingAttributes,
    data: attributeRes,
    refetch: refetchAttributes,
  } = useQuery(ATTRIBUTES, {
    variables: { clientCode: client?.apiId },
  });

  useEffect(() => {
    if (client) {
      refetchAttributes();
    }
  }, [client, refetchAttributes]);

  useEffect(() => {
    const attributeData = attributeRes ? attributeRes?.attributes?.data : [];
    const attributes = attributeData || [];

    if (!_.isEqual(context.attributes, attributes)) {
      setContext({ ...context, attributes });
    }
  }, [attributeRes, setContext]);

  const entityDataList = useMemo(
    () => normalizeEntity(attributes),
    [attributes]
  );

  useEffect(() => {
    const treeDataValue = treeData(
      entityDataList,
      'name',
      menu,
      expandedKeys,
      selectedKeys
    );

    setState((prevState) => ({
      ...prevState,
      nodes: treeDataValue,
    }));
  }, [entityDataList, expandedKeys, selectedKeys]);

  const onClickSubmenu = (mode, id) => {
    handleClickSubmenu(mode, id);
  };

  const handleSelectAttribute = (event) => {
    setContext({ ...context, selectedAttributeId: event.node.key });
  };

  const menu = (id) => {
    const attribute = attributes.find((item) => item._id === id);
    const canAdd = attribute?.core?.parentId === null;
    return (
      <Menu>
        {canAdd && (
          <Menu.Item
            key="bulk_create"
            onClick={() => onClickSubmenu('bulk_create', id)}
          >
            Create New Attributes
          </Menu.Item>
        )}
        <Menu.Item key="edit" onClick={() => onClickSubmenu('edit', id)}>
          Edit
        </Menu.Item>
        <Menu.Item key="delete" onClick={() => onClickSubmenu('delete', id)}>
          Delete
        </Menu.Item>
      </Menu>
    );
  };

  const onSelect = (selectedKeys, info) => {
    handleSelectAttribute(info);
    handleSelectedKeys(selectedKeys);
  };

  const onExpand = (expandedKeys) => {
    handleExpandedKeys(expandedKeys);
  };

  /**
   * Process tree state change
   * @param nodes
   */
  const handleChange = (nodes) => {
    setState((prevState) => ({ ...prevState, nodes }));
    const expandedKeysList = flattenNodes(nodes)
      .filter((item) => item.state.expanded === true)
      .map((item) => item.id);

    onExpand(expandedKeysList);
  };

  /**
   * Handle node click
   * @param nodeId
   */
  const handleNodeSelect = (nodeId) => {
    onSelect([nodeId], { node: { key: nodeId } });
  };

  return (
    <div className="taxonomy-sidebar-tree-content">
      <Spin spinning={loadingAttributes}>
        <div className="taxonomy-total-count">
          <Typography>{entityDataList?.length} Attributes</Typography>
        </div>
        <div className="taxonomy-sidebar-virtual-tree-wrapper">
          <Tree nodes={state.nodes} onChange={handleChange}>
            {({ style, node, ...rest }) => (
              <div style={style}>
                <Expandable
                  node={node}
                  {...rest}
                  iconsClassNameMap={{
                    collapsed: 'mi mi-keyboard-arrow-right',
                    expanded: 'mi mi-keyboard-arrow-down',
                    lastChild: 'mi mi-insert-drive-file',
                  }}
                >
                  <Dropdown overlay={menu(node.id)} trigger="contextMenu">
                    <Button
                      type="text"
                      onClick={() => handleNodeSelect(node.id)}
                      style={{
                        padding: 0,
                        transform: 'translateY(-7px)',
                      }}
                      className={
                        selectedKeys.includes(node.id) ? 'selected' : ''
                      }
                    >
                      {node.name}
                    </Button>
                  </Dropdown>
                </Expandable>
              </div>
            )}
          </Tree>
        </div>
      </Spin>
    </div>
  );
};

SidebarAttributesTree.propTypes = {
  expandedKeys: PropTypes.array,
  selectedKeys: PropTypes.array,
  handleSelectedKeys: PropTypes.func,
  handleExpandedKeys: PropTypes.func,
  handleClickSubmenu: PropTypes.func,
};

SidebarAttributesTree.defaultProps = {
  selectedKeys: [],
  expandedKeys: [],
  handleSelectedKeys: () => {},
  handleExpandedKeys: () => {},
  handleClickSubmenu: () => {},
};

export default SidebarAttributesTree;
